export interface Product {
  /* Infos minimum d'un produit */
  code: string;
  productId: number;
  type: number;
  typeCategory: number;
  labelHtml: string;
  code13: string;
  deletedDate: string;
  deleted: boolean;
  replacementProductId: number;
  replacementProductLabel: string;

  /* Commun api search / api infos */
  labels: ProductLabels;
  properties: ProductProperties;
  prices: ProductPrices;

  /* Données spécifique à l'api search */
  searchProperties: ProductSearchProperties;

  /* Données spécifique à l'api infos */
  extendedProperties: ProductExtendedProperties;
}

export interface ProductAtcClass {
  code: string;
  label: string;
}

export interface ProductCodes {
  cip7: string;
  cip13: string;
  cip13Referent: string;
  ucd7: string;
  ucd13: string;
  cis: string;
  code13Referent: string;
  ean13: ProductEan13[];
  udi: ProductUDI[];
}

export interface ProductUDI {
  order: string;
  udiType: string;
  udi: string;
}

export interface ProductComponent {
  code: string;
  label: string;
  parentCode: string;
}

export interface ProductEan13 {
  code: string;
}

export interface ProductExtendedFlags {
  bloodDerivative: number;
  anticoagulant: number;
  vaccin: number;
  soluteDilution: number;
  emergencyContraceptive: number;
  minorContraceptive: number;
  fridge: number;
  humanAntibiotic: number;
  veterinaryAntibiotic: number;
  veterinaryPrescription: number;
  deconditionning: number;
  seasonalFluVaccine: number;
  ghs: number;
  horsGhs: number;
  infertilityTreatment: number;
  mindalteringSubstance: number;
  testReagent: number;
  firstAgeMilk: number;
  abortionDrug: number;
  mte: number;
  tfr: number;
  biosimilarReferent: number;
  hybridReferent: number;
  freezer: number;
  reinforcedMonitoring: number;
  t2a: number;
  communityApproval: number;
  drugException: number;
  pregnancyPictogram: number;
}

export interface ProductExtendedProperties {
  brandCode: string;
  brandLabel: string;
  brandLabelHtml: string;
  rangeCode: string;
  rangeLabel: string;
  rangeLabelHtml: string;
  biosimilarGroupLabel: string;
  numberOfUcds: number;
  listCode: string;
  listLabel: string;
  statusCode: number;
  statusLabel: string;
  commonNameCode: string;
  commonNameLabel: string;
  revisionDate: string;
  segmentationStatus: string;
  segmentationStatusHtml: string;
  actCode: string;
  actLabelHtml: string;
  ammNumber: string;
  segments: string;
  segmentsHtml: string;
  equivalentDosage: string[];
  genericGroups: ProductGenericGroup[];
  ammDate: string;
  ammRevisionDate: string;
  composition: ProductComponents;
  holderLab: ProductLaboratory;
  operatingLab: ProductLaboratory;
  chapters: ProductMonographChapters[];
  paradocChapters: ProductMonographChapters[];
  atcClass: ProductClass[];
  bcbClass: ProductClass[];
  routes: ProductRouteAdministration[];
  prescriptibles: ProductPrescriptible;
  restrictedPrescription: ProductRestrictedPrescription[];
  documents: ProductDocument[];
  segmentationAttributes: ProductSegmentationAttributes;
  keywords: ProductKeyword;
  crushability: ProductCrushability;
  specialPrescription: ProductSpecialPrescription[];
  photo: string;
  sam: ProductSam;
  creationDate: string;
  pricesHistory: PriceHistory[];
  identifier: Identifier;
}

export interface Identifier {
  numberOfPresentations: string;
  packagingLabel: string;
  presentationLabel: string;
  unitPresentationLabel: string;
  unitPresentationLabelPlural: string;
  unitsPerPresentation: string;
}

export interface PriceHistory {
  applicationDate: string;
  correctedOpposabilityDate: string;
  correctedValue: string;
  correctionSource: string;
  field: string;
  joDate: string;
  nor: string;
  opposabilityDate: string;
  sourceLabel: string;
  value: string;
}

export interface ProductFlags {
  misuse: number;
  careBasket: number;
  doping: number;
  driveWarning: string;
  narcotic: number;
  crushable: number;
  referent: number;
  generic: number;
  biosimilar: number;
  hybrid: number;
  hospitalDelivery: number;
  internal: number;
  medicalDevice: number;
  homeo: number;
  pediatrics: number;
  extendedFlags: ProductExtendedFlags | null;
}

export interface ProductIntakeUnit {
  order: number;
  code: number;
  labelHtml: string;
  pluralLabelHtml: string;
  shortLabelHtml: string;
  intakeUnitNumber: string;
  dispensationNumber: string;
  distributionNumber: string;
  round: number;
  throwTheRest: number;
}

export interface ProductLabels {
  typeLabel: string;
  label: string;
  shortLabel: string;
  shortLabelHtml: string;
  longLabel: string;
  longLabelHtml: string;
  searchLabel: string;
}

export interface ProductPrices {
  ucdPrice: string;
  repaymentRate: number;
  vatRate: string;
  reimbursementBase: string;
  sellingPrice: string;
  tfr: string;
}

export interface ProductProperties {
  thumbnail: string;
  productName: string;
  productDosage: string;
  galenicFormCode: string;
  galenicFormLabelHtml: string;
  galenicFormLabel: string;
  aspect: string;
  inFacilityFormulary: boolean;
  codes: ProductCodes;
  flags: ProductFlags;
  segmentationAttributes: string[];
  excipientsWithKnowEffects: ProductComponent[];
  atcClasses: ProductAtcClass[];
  intakeUnits: ProductIntakeUnit[];
  subLaboratories: SubLabo[];
  lpp: ProductLpp[] | undefined;
  virtualDrug: ProductVirtualDrug | null;
}

export interface ProductVirtualDrug {
  virtualDrugCode: number;
  virtualDrugLabel: string;
}
export interface ProductSearchProperties {
  score: number | null;
  safeScore: number;
  safeDetails: SafeSearch[];
  samCode: string;
  samPatient: boolean;
}

export interface SubLabo {
  code: number;
  label: string;
}

export interface SafeSearch {
  codeAlerte: number;
  libelleAlerte: string;
  typeAlerte: string;
  niveauAlerte: number;
  idAlerte: string;
  productId2: number;
  productLabel2: string;
  details: string;
}

export interface ProductClass {
  code: string;
  label: string;
  codeLvl1: string;
  labelLvl1: string;
  codeLvl2: string;
  labelLvl2: string;
  codeLvl3: string;
  labelLvl3: string;
  codeLvl4: string;
  labelLvl4: string;
  codeLvl5: string;
  labelLvl5: string;
  codeLvl6: string;
  labelLvl6: string;
  codeLvl7: string;
  labelLvl7: string;
  notSubstituable: number;
}

export interface ProductComponents {
  activeSubstances: ProductComponent[];
  excipients: ProductComponent[];
  excipientsWithKnownEffects: ProductComponent[];
}

export interface ProductCrushability {
  unpleasantTaste: number;
  crushable: number;
  openable: number;
  disintegrationPossible: number;
  canBeTakenWithWater: number;
  canBeTakenWithOrangeJuice: number;
  canBeTakenWithMilk: number;
  canBeTakenWithYogurt: number;
  canBeTakenWithCompote: number;
  immediateMedicationPossible: number;
  informations: string;
}

export interface ProductDocument {
  code: string;
  name: string;
  extension: string;
  source: string;
  date: string;
  title: string;
  titleHtml: string;
  category: string;
  categoryHtml: string;
  link: string;
}

export interface ProductGenericGroup {
  genericGroupCode: string;
  genericGroupLabel: string;
  genericGroupLabelHtml: string;
}

export interface ProductKeyword {
  indicationsKeywords: ProductKeywordDetail[];
  contraindicationsKeywords: ProductKeywordDetail[];
  contraindicationsNotRecommendedKeywords: ProductKeywordDetail[];
  sideEffectsKeywords: ProductKeywordDetail[];
  precautionsForUseKeywords: ProductKeywordDetail[];
}

export interface ProductKeywordDetail {
  code: string;
  label: string;
  labelHtml: string;
  frequencyCode: string;
  frequencyLabel: string;
  frequencyLabelHtml: string;
  socCode: string;
  socLabel: string;
  socShortLabel: string;
  glossary: number;
}

export interface ProductLaboratory {
  code: string;
  label: string;
  address: string;
  zipCode: string;
  town: string;
  website: string;
  email: string;
  phone: string;
  fax: string;
  country: string;
}

export interface ProductLpp {
  code: string;
  label: string;
  order: string;
  coefficient: string;
  text: string;
  qualifying: string;
}

export interface ProductMonographChapters {
  id: string;
  label: string;
  text: string;
  source: string; //specific to UI
}

export interface ProductPrescriptible {
  doctor: string;
  veterinary: string;
  occupationalTherapist: string;
  nurse: string;
  podiatrist: string;
  physiotherapist: string;
  midwifeForWomen: string;
  midwifeForNewborns: string;
  speechTherapist: string;
  orthoptist: string;
  midwifeForPatient: string;
  advancedPracticeNurse: string;
  dentist: string;
  biologist: string;
  pharmacist: string;
}

export interface ProductRestrictedPrescription {
  code: string;
  title: string;
  titleHtml: string;
  decree: string;
  decreeHtml: string;
}

export interface ProductRouteAdministration {
  code: string;
  label: string;
}

export interface ProductSam {
  code: string;
  name: string;
  author: string;
  coAuthor: string;
  referencingDate: string;
  title: string;
  requester: string;
  hospitalLAP: number;
  outpatientLAP: number;
  pharmacyLAD: number;
  puiLAD: number;
  validationDate: string;
  updateDate: string;
  versionNumber: string;
  status: string;
  source: string;
  contact: string;
  logo: string;
  pdf: string;
  hospitalLAPText: string;
  outpatientLAPText: string;
  pharmacyLADText: string;
}

export interface ProductSegmentationAttributes {
  others: string[];
  patientGrounds: string[];
  distributionChannels: string[];
}

export interface ProductSpecialPrescription {
  label: string;
  speciality: string;
}

export interface SimpleProduct {
  productId: string;
  libelle: string;
  cip: string;
}